import { jsxs, jsx } from 'react/jsx-runtime';
import { IconDesktop16MiscPlus, IconTextEditorInEditorDragHandle } from '@fabric-app/icons/svg';
import { ButtonIcon } from '@fabric-app/ui/components/Button';
import { Flex } from '@fabric-app/ui/components/Flex';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { reactToHTML } from '@fabric-app/ui/utils/reactToHTML';
import { keyframes, styled } from 'styled-components';
import { NodeControlMeta } from './NodeControlMeta.js';
import { NodeControlPluginKey, CustomTextSelection } from './NodeControlPlugin.js';
import { createPreviewContainer, generatePreview } from '../../utils/generatePreview.js';
import { getPath } from '../utils.js';
import { TextSelection } from '@tiptap/pm/state';
import { ContextualMenuPluginKey } from '../ContextualMenu/ContextualMenuPlugin.js';
import { ContextualMenuMeta } from '../ContextualMenu/ContextualMenuMeta.js';
import { ContextualMenuSpecs } from '../ContextualMenu/ContextualMenuConfig.js';
import { isDefined } from '@fabric-app/utils/guards';

const fadeIn = keyframes([
  `from{opacity:0;}to{opacity:1;}`
]);
const Group = styled(Flex).withConfig({
  displayName: "NodeControlWidget__Group",
  componentId: "sc-497350e2-0"
})([
  `user-select:none !important;animation:`,
  ` 0.2s ease-in-out;z-index:1;gap:2px;position:absolute;transform:translateX(calc(-100% - var(--node-control-offset-x,0px) - 0.175rem)) translateY(var(--node-control-offset-y,0px));`
], fadeIn);
const EditorButton = styled(ButtonIcon).attrs((p) => {
  return {
    ...p,
    size: "xss",
    shape: "square"
  };
}).withConfig({
  displayName: "NodeControlWidget__EditorButton",
  componentId: "sc-497350e2-1"
})([
  `padding:0.125rem;color:`,
  `;`
], cssVar["color-text-placeholder"]);
const DragButton = styled(EditorButton).withConfig({
  displayName: "NodeControlWidget__DragButton",
  componentId: "sc-497350e2-2"
})([
  `position:relative;overflow:visible;&::before{content:'';display:block;position:absolute;top:0;bottom:0;left:-2px;width:2px;}cursor:grab;`
]);
const NodeControlContentRender = reactToHTML(/* @__PURE__ */ jsxs(Group, {
  children: [
    /* @__PURE__ */ jsx(EditorButton, {
      "data-node-add-button": true,
      children: /* @__PURE__ */ jsx(IconDesktop16MiscPlus, {})
    }),
    /* @__PURE__ */ jsx(DragButton, {
      "data-node-drag-handle": true,
      draggable: true,
      children: /* @__PURE__ */ jsx(IconTextEditorInEditorDragHandle, {})
    })
  ]
}));
const configureDragData = (e, container) => {
  if (!e.dataTransfer) {
    return;
  }
  e.dataTransfer.clearData();
  e.dataTransfer.setData("text/plain", container.innerHTML);
  e.dataTransfer.setData("text/html", container.innerHTML);
  e.dataTransfer.setDragImage(container, 0, 0);
};
const cleanupDrag = (view) => {
  NodeControlMeta.dispatchReset(view);
};
const setupDragPreview = (e, view) => {
  if (!view.editable || !e.dataTransfer) {
    return cleanupDrag(view);
  }
  const state = NodeControlPluginKey.getState(view.state);
  if (!state || !state.nodePos || !state.nodeStart) {
    return cleanupDrag(view);
  }
  const { nodeStart } = state;
  const resolved = view.state.doc.resolve(nodeStart);
  const { node: element } = view.domAtPos(resolved.start());
  if (!(element instanceof HTMLElement)) {
    return cleanupDrag(view);
  }
  const container = createPreviewContainer();
  const nodeType = resolved.node().type.name;
  const previewContent = generatePreview(element, view.dom, nodeType);
  if (!previewContent) {
    return cleanupDrag(view);
  }
  container.appendChild(previewContent);
  container.style.paddingLeft = "1rem";
  document.body.appendChild(container);
  configureDragData(e, container);
  const { tr } = view.state;
  const start = resolved.node().type.name === "codeBlock" ? resolved.start() - 1 : resolved.start() - 1;
  const selection = CustomTextSelection.create(view.state.doc, start, resolved.end());
  const slice = selection.content();
  view.dragging = {
    slice,
    move: e.ctrlKey
  };
  tr.setSelection(selection);
  view.dispatch(tr);
  document.addEventListener("drop", () => cleanupDrag(view), {
    once: true
  });
  document.addEventListener("dragend", () => cleanupDrag(view), {
    once: true
  });
};
const NodeControlWidget = (view, _getPos) => {
  const div = document.createElement("div");
  div.dataset.nodeControl = "true";
  div.classList.add("fbc-node-control");
  div.innerHTML = NodeControlContentRender;
  div.dataset.tiptapToolbar = "true";
  const dragButton = div.querySelector("[data-node-drag-handle]");
  const addButton = div.querySelector("[data-node-add-button]");
  const content = div.firstElementChild;
  if (!dragButton || !addButton || !content) {
    throw new Error("NodeControlWidget: dragButton or addButton or content not found");
  }
  const onDragHandleMouseDown = () => {
    const inlineToolbar = document.querySelector(".tiptap-inline-toolbar");
    if (inlineToolbar) {
      const event = new MouseEvent("mousedown", {
        bubbles: true,
        cancelable: true,
        view: window,
        button: 0
      });
      inlineToolbar.dispatchEvent(event);
    }
  };
  const onDragHandleDragEnd = () => {
    NodeControlMeta.dispatchReset(view);
  };
  const onDragHandleDragStart = (e) => {
    content.style.opacity = "0";
    setupDragPreview(e, view);
  };
  const preventDefault = (e) => {
    e.preventDefault();
  };
  const onClickAddButton = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const state = NodeControlPluginKey.getState(view.state);
    if (!state || !isDefined(state.nodePos) || !isDefined(state.nodeStart)) {
      return;
    }
    const resolved = view.state.doc.resolve(state.nodeStart);
    const path = getPath(resolved);
    if (path.length < 2) return [];
    let target = path[0];
    let targetParent = path[1];
    let replacement = false;
    if (targetParent.node.type.isInGroup("list") && target.index === 0) {
      target = targetParent;
      targetParent = path[2];
      replacement = false;
    } else if (target.node.inlineContent && target.node.textContent.length < 1) {
      replacement = true;
    } else {
      replacement = false;
    }
    const tr = view.state.tr;
    const insertPosition = replacement ? target.start : target.after;
    const selectPosition = replacement ? insertPosition : insertPosition + 1;
    tr.setMeta(ContextualMenuPluginKey, new ContextualMenuMeta(ContextualMenuSpecs.addNode, selectPosition, selectPosition, true));
    if (!replacement) {
      tr.insert(insertPosition, view.state.schema.nodes["paragraph"].create());
    }
    tr.setSelection(TextSelection.create(tr.doc, selectPosition));
    view.dispatch(tr);
  };
  const onClickDragButton = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const state = NodeControlPluginKey.getState(view.state);
    if (!state || !isDefined(state.nodePos) || !isDefined(state.nodeStart)) {
      return;
    }
    const resolved = view.state.doc.resolve(state.nodeStart);
    const path = getPath(resolved);
    if (path.length < 2) return [];
    const target = path[0];
    const tr = view.state.tr;
    const selection = view.state.selection;
    if (selection.from >= target.end || selection.to <= target.start) {
      tr.setSelection(TextSelection.create(tr.doc, target.start));
    }
    tr.setMeta(ContextualMenuPluginKey, new ContextualMenuMeta(ContextualMenuSpecs.modifyItem, target.start, target.end - 1, true));
    view.dispatch(tr);
  };
  dragButton.addEventListener("mousedown", onDragHandleMouseDown);
  dragButton.addEventListener("dragstart", onDragHandleDragStart);
  dragButton.addEventListener("dragend", onDragHandleDragEnd);
  addButton.addEventListener("click", onClickAddButton);
  dragButton.addEventListener("click", onClickDragButton);
  addButton.addEventListener("mousedown", preventDefault);
  div.destroy = () => {
    dragButton.removeEventListener("mousedown", onDragHandleMouseDown);
    dragButton.removeEventListener("dragstart", onDragHandleDragStart);
    dragButton.removeEventListener("dragend", onDragHandleDragEnd);
    addButton.removeEventListener("click", onClickAddButton);
    dragButton.removeEventListener("click", onClickDragButton);
    addButton.removeEventListener("mousedown", preventDefault);
  };
  return div;
};

export { NodeControlWidget };
//# sourceMappingURL=NodeControlWidget.js.map
